import { partyAccessDecisionOf, } from "./access.js";
import { isWithheldParty } from "./leg.js";
import { perCallerLimitOn } from "./setting.js";
/** Rejected without answering (Stage A); unbilled. */
export const PARTY_REJECT_REASON = ["party_blocked", "party_backstop_rejected"];
/** Refused with one fixed line (Stage B), then the hang-up; unbilled, never queued, never message-taking. */
export const PARTY_REFUSE_REASON = ["withheld_party_refused", "party_not_listed", "party_limit_reached"];
/**
 * The language-pack keys caller protection speaks (CP-LANG's addendum keys, `registeredOn: "2026-10-27"`, design
 * §40.13.3). `@wetalk/domain` cannot import `@wetalk/lang`, so they are string literals here; the suite pins them to the
 * pack's registry, and the runtime checks them against `PlatformPhraseAddendumKey` where both are importable.
 */
export const PARTY_PROTECTION_LINE_KEY = [
    "party.limit_reached",
    "party.withheld_refused",
    "party.not_listed",
    "conversation.longest_final_minute",
];
/** The one line each refusal speaks (the `party_refused` plan's `lineKey`). */
export const PARTY_REFUSAL_LINE_KEY = Object.freeze({
    withheld_party_refused: "party.withheld_refused",
    party_not_listed: "party.not_listed",
    party_limit_reached: "party.limit_reached",
});
/** The longest conversation's final-minute line (design §40.6). A language without it skips it and still ends. */
export const LONGEST_CONVERSATION_FINAL_MINUTE_LINE_KEY = "conversation.longest_final_minute";
const EXEMPT = Object.freeze({ kind: "exempt" });
function reject(reason) {
    return Object.freeze({ kind: "reject", reason });
}
function refuse(reason) {
    return Object.freeze({ kind: "refuse", reason, lineKey: PARTY_REFUSAL_LINE_KEY[reason] });
}
function accessOf(leg, input, switches, signedVisitor) {
    return partyAccessDecisionOf({
        access: input.access,
        accessEntryPresent: input.accessEntryPresent,
        entryMatch: input.accessEntryMatch,
        leg,
        audience: leg.audience,
        signedVisitor,
        internalLockOn: switches.internalLockOn,
    });
}
/**
 * Stage A (§40.3.2): steps (0), (1) and (4) — before the carrier answers, or between the SIP row and the bridge. An
 * admission here carries no reservation (Stage B takes it) and the access decision as Stage A could see it.
 */
export function stageAOf(leg, input, backstop, switches) {
    if (leg.isTest)
        return EXEMPT;
    if (switches.costOn && backstop.kind === "tripped")
        return reject("party_backstop_rejected");
    if (switches.costOn && input.blocked)
        return reject("party_blocked");
    return Object.freeze({ kind: "admit", reservation: null, access: accessOf(leg, input, switches, null) });
}
/**
 * The steps before (5), from the same inputs: `null` when they admit. Stage B and {@link stageBNeedsReservation} share
 * it, so the reserve is asked only for a party that would otherwise be admitted.
 */
function refusalBeforeReservation(leg, input, switches, access) {
    if (switches.costOn && input.protection.withheldPartyRefusal && isWithheldParty(leg))
        return refuse("withheld_party_refused");
    if (access.kind === "not_listed")
        return refuse("party_not_listed");
    // Stage A rejected a blocked voice leg already; a block read here (no Stage A ran) still rejects, as the order says.
    if (switches.costOn && input.blocked)
        return reject("party_blocked");
    return null;
}
/**
 * Whether Stage B must ask the store to reserve (step (5) applies): not a test, the cost switch on, a per-caller window
 * set, and steps (2)–(4) admit. RT-PP reserves only then, with the conversation id as the reservation id, so a refused
 * party never takes a count it would have to give back.
 */
export function stageBNeedsReservation(leg, input, switches, signedVisitor) {
    if (leg.isTest || !switches.costOn || !perCallerLimitOn(input.protection))
        return false;
    return refusalBeforeReservation(leg, input, switches, accessOf(leg, input, switches, signedVisitor)) === null;
}
/**
 * Stage B (§40.3.3): steps (0), (2), (3) and (5) — first in the media loop, at the chat socket's `hello` and first
 * admission. `reservation` is what the store answered (`not_reserved` when it was not asked). An admission carries the
 * reservation to every exit (§40.4.3).
 */
export function stageBOf(leg, input, reservation, switches, signedVisitor) {
    if (leg.isTest)
        return EXEMPT;
    const access = accessOf(leg, input, switches, signedVisitor);
    const before = refusalBeforeReservation(leg, input, switches, access);
    if (before !== null)
        return before;
    if (switches.costOn && perCallerLimitOn(input.protection) && reservation.kind === "limit_reached") {
        return refuse("party_limit_reached");
    }
    return Object.freeze({
        kind: "admit",
        reservation: reservation.kind === "reserved" ? reservation.reservation : null,
        access,
    });
}
/** The two stages composed: Stage A, and Stage B only after Stage A admitted. The order of the table above. */
export function partyVerdictOf(leg, input, answer, switches, signedVisitor) {
    const stageA = stageAOf(leg, input, answer.backstop, switches);
    if (stageA.kind !== "admit")
        return stageA;
    return stageBOf(leg, input, answer.reservation, switches, signedVisitor);
}
/** Whether a verdict is a customer-rule refusal or rejection (History's and Analytics' refusals; never `fail`). */
export function isCustomerRuleVerdict(verdict) {
    return verdict.kind === "reject" || verdict.kind === "refuse";
}
/** The five end reasons of a customer-rule refusal or rejection (design §40.12.1): unbilled, `answered_at` NULL. */
export const PARTY_REFUSAL_END_REASON = [...PARTY_REJECT_REASON, ...PARTY_REFUSE_REASON];
/** All seven end reasons caller protection writes: the five refusals, and the two of an admitted, billed conversation. */
export const PARTY_PROTECTION_END_REASON = [
    ...PARTY_REFUSAL_END_REASON,
    "silent_start_ended",
    "longest_conversation_reached",
];
//# sourceMappingURL=verdict.js.map